import React, { useEffect, useCallback } from "react";
import Papa from "papaparse";

/**
 * Specialized CSV Parser Component
 *
 * Handles CSV file parsing with configurable field mapping and data transformation.
 *
 * @param {Object} props - Component props
 * @param {File} props.file - The CSV file to parse
 * @param {Object} props.config - Configuration object for parsing
 * @param {Function} props.onDataParsed - Callback when data is successfully parsed
 * @param {Function} props.onError - Callback when parsing fails
 */
const CSVParser = ({ file, config, onDataParsed, onError }) => {
  /**
   * Map CSV data to form structure using configuration
   */
  const mapDataToForm = useCallback(
    (row) => {
      if (!config?.columns) return row;

      const mappedData = {};

      Object.entries(config.columns).forEach(([key, columnConfig]) => {
        const header = columnConfig.possibleHeaders?.find(
          (h) => row[h] !== undefined
        );
        if (
          header &&
          row[header] !== undefined &&
          row[header] !== null &&
          row[header] !== ""
        ) {
          let value = row[header];

          // Apply transformation if provided
          if (
            columnConfig.transform &&
            typeof columnConfig.transform === "function"
          ) {
            try {
              value = columnConfig.transform(value);
            } catch (error) {
              console.warn(`Transform error for ${key}:`, error);
            }
          }

          mappedData[key] = value;
        }
      });

      return mappedData;
    },
    [config]
  );

  /**
   * Parse CSV file
   */
  const parseCSV = useCallback(() => {
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          if (results.errors.length > 0) {
            throw new Error(
              `CSV parsing errors: ${results.errors
                .map((e) => e.message)
                .join(", ")}`
            );
          }

          const extractedData = results.data.map((row) => mapDataToForm(row));

          // Console summary (no raw values) to quickly see "final extraction result" in DevTools.
          // NOTE: Avoids logging PII; only logs counts/keys.
          try {
            console.info("[CSVParser] extraction complete", {
              fileName: file?.name,
              extractedDataCount: extractedData.length,
              sampleKeys: extractedData[0]
                ? Object.keys(extractedData[0]).slice(0, 20)
                : [],
            });
          } catch {
            // best-effort logging only
          }

          // Call success callback
          onDataParsed?.(extractedData, results.data);
        } catch (error) {
          onError?.(error);
        }
      },
      error: (error) => {
        onError?.(new Error(`CSV parsing failed: ${error.message}`));
      },
    });
  }, [file, mapDataToForm, onDataParsed, onError, config]);

  // Auto-parse when file is provided
  useEffect(() => {
    if (file) {
      parseCSV();
    }
  }, [file, parseCSV]);

  // This component doesn't render anything - it's purely functional
  return null;
};

export default CSVParser;
